import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { LogOut, RefreshCw, Trash2, Inbox } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { LogoMark } from "@/components/Logo";
import { getToken, formatApiError } from "@/pages/AdminLogin";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  new: "bg-amber-50 text-amber-600 border-amber-200",
  contacted: "bg-sky-50 text-sky-600 border-sky-200",
  closed: "bg-gray-100 text-gray-500 border-gray-200",
};

const fmtDate = (iso) => {
  try {
    return new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch {
    return iso;
  }
};

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [subs, setSubs] = useState(null);
  const [error, setError] = useState(null);

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/submissions`, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setSubs(data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        localStorage.removeItem("vertex_admin_token");
        navigate("/admin", { replace: true });
        return;
      }
      setError(formatApiError(err.response?.data?.detail));
      setSubs([]);
    }
  };

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin", { replace: true });
      return;
    }
    load();
  }, [navigate]);

  const updateStatus = async (id, status) => {
    try {
      const { data } = await axios.patch(`${API}/submissions/${id}`, { status }, {
        headers: { Authorization: `Bearer ${getToken()}` },
      });
      setSubs((prev) => prev.map((s) => (s.id === id ? data : s)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this submission permanently?")) return;
    try {
      await axios.delete(`${API}/submissions/${id}`, { headers: { Authorization: `Bearer ${getToken()}` } });
      setSubs((prev) => prev.filter((s) => s.id !== id));
      toast.success("Submission deleted");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const logout = () => {
    localStorage.removeItem("vertex_admin_token");
    navigate("/admin", { replace: true });
  };

  return (
    <div className="min-h-screen bg-[#F7F7F8] text-gray-900">
      <header className="glass sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <LogoMark className="w-8 h-8" />
            <div>
              <div className="font-display font-extrabold tracking-tight leading-none text-gray-900">
                VERTEX<span className="text-crimson">.</span> Studio
              </div>
              <div className="font-mono text-[10px] tracking-widest text-gray-400 uppercase mt-1">Client Briefs</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              data-testid="admin-refresh-button"
              onClick={load}
              className="w-10 h-10 rounded-full border border-gray-200 bg-white flex items-center justify-center text-gray-500 hover:text-crimson hover:border-crimson transition-colors duration-300"
              aria-label="Refresh"
            >
              <RefreshCw className="w-4 h-4" />
            </button>
            <button
              type="button"
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-gray-200 bg-white text-sm text-gray-600 hover:text-crimson hover:border-crimson transition-colors duration-300"
            >
              <LogOut className="w-4 h-4" /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="flex items-end justify-between mb-8">
          <h1 className="font-display font-extrabold text-2xl md:text-3xl tracking-tight text-gray-900">
            Requirement Submissions
          </h1>
          {subs && (
            <span className="font-mono text-xs tracking-widest text-gray-400" data-testid="admin-submissions-count">
              {subs.length} TOTAL
            </span>
          )}
        </div>

        {subs === null && !error && (
          <div className="py-24 text-center text-gray-400 text-sm">Loading briefs…</div>
        )}

        {error && (
          <div className="rounded-2xl border border-crimson/30 bg-crimson/5 p-6 text-sm text-gray-800" data-testid="admin-error-panel">
            {error}
          </div>
        )}

        {subs && subs.length === 0 && !error && (
          <div className="rounded-3xl border border-dashed border-gray-200 bg-white py-24 flex flex-col items-center gap-4 text-center" data-testid="admin-empty-state">
            <Inbox className="w-8 h-8 text-gray-300" />
            <p className="text-gray-400 text-sm max-w-xs">
              No briefs yet. New client requirements submitted on the site will appear here.
            </p>
          </div>
        )}

        <div className="space-y-4">
          {subs &&
            subs.map((s) => (
              <div
                key={s.id}
                data-testid={`submission-card-${s.id}`}
                className="rounded-2xl border border-gray-100 bg-white shadow-soft p-6 grid md:grid-cols-12 gap-5"
              >
                <div className="md:col-span-3">
                  <div className="font-display font-bold text-gray-900">
                    {s.first_name} {s.last_name}
                  </div>
                  <div className="text-xs text-gray-400 mt-1">{s.designation}</div>
                  <div className="text-xs text-gray-400">{s.company}</div>
                  <span
                    className={`inline-block mt-3 px-3 py-1 rounded-full border font-mono text-[10px] tracking-widest uppercase ${STATUS_STYLES[s.status] || STATUS_STYLES.new}`}
                    data-testid={`submission-status-${s.id}`}
                  >
                    {s.status}
                  </span>
                </div>
                <div className="md:col-span-3 text-sm space-y-1.5">
                  <div className="font-mono text-[10px] tracking-widest text-crimson uppercase" data-testid={`submission-trackcode-${s.id}`}>
                    {s.track_code}
                  </div>
                  <div className="text-gray-700 break-all" data-testid={`submission-email-${s.id}`}>{s.email}</div>
                  <div className="text-gray-500 font-mono text-xs">+91 {s.phone}</div>
                  <div className="font-mono text-[10px] tracking-widest text-gray-400 uppercase mt-2">{fmtDate(s.created_at)}</div>
                </div>
                <div className="md:col-span-4 text-sm">
                  <div className="flex flex-wrap gap-2">
                    <span className="px-3 py-1 rounded-full bg-gray-50 border border-gray-200 text-[11px] text-gray-700">{s.service}</span>
                    <span className="px-3 py-1 rounded-full bg-crimson/5 border border-crimson/25 text-[11px] text-gray-900 font-mono">₹ {s.budget}</span>
                  </div>
                  {s.brief && <p className="text-gray-500 text-xs leading-relaxed mt-3 line-clamp-3">{s.brief}</p>}
                </div>
                <div className="md:col-span-2 flex md:flex-col items-start md:items-end gap-3">
                  <Select value={s.status} onValueChange={(v) => updateStatus(s.id, v)}>
                    <SelectTrigger
                      data-testid={`submission-status-select-${s.id}`}
                      className="h-8 rounded-full border-gray-200 bg-gray-50 px-3 text-[11px] text-gray-800"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-gray-100 text-gray-900">
                      <SelectItem value="new">New</SelectItem>
                      <SelectItem value="contacted">Contacted</SelectItem>
                      <SelectItem value="closed">Closed</SelectItem>
                    </SelectContent>
                  </Select>
                  <button
                    type="button"
                    data-testid={`submission-delete-button-${s.id}`}
                    onClick={() => remove(s.id)}
                    className="w-8 h-8 rounded-full border border-gray-200 flex items-center justify-center text-gray-400 hover:text-crimson hover:border-crimson transition-colors duration-300"
                    aria-label="Delete submission"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            ))}
        </div>
      </main>
    </div>
  );
}
